import React from "react";
import { connect } from "react-redux";
import {
  Card,
  CardContent,
  Button,
  TextField,
  FormControl,
  InputAdornment,
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import IconButton from "@mui/material/IconButton";
import ptsLogo from "../assets/images/PTS-logo.png";
import IconAms from "../assets/images/icon-AMS.svg";
import splashScreen from "../assets/images/login-splash.jpg";
import {
  sendPassResetLink,
  setPasswordFieldShouldReset,
  submitLoginForm,
} from "../reducers/UserReducer";
import { validateEmail } from "../utils/functions";
import { notify } from "../reducers/NotifierReducer";
import { LOGIN_VIEW, PASSWORD_RESET_VIEW } from "../utils/constants";
import { checkForUpdates } from "../reducers/AppInfoReducer";

const useStyles = makeStyles((theme) => ({
  pageWrap: {
    width: "100%",
    height: "100vh",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    backgroundImage: `url(${splashScreen})`,
    backgroundSize: "cover",
    backgroundPosition: "center",
  },
  card: {
    width: 480,
    maxWidth: "95%",
    background: "fff",
    "& img": {
      width: "100%",
    },
  },
  content: {
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  branding: {
    width: "100%",
    height: 200,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    background: "#52134E",
    "& img": {
      width: 150,
      height: "auto",
    },
  },
  branding2: {
    width: "100%",
    textAlign: "center",
    padding: "25px 0 5px",
    "& img": {
      height: 50,
      width: "auto",
    },
  },
  actions: {
    textAlign: "center",
    marginTop: 30,
    "& button": {
      background: "#52134E",
      color: "#fff",
      paddingLeft: "4em",
      paddingRight: "4em",
      textTransform: "uppercase",
      "&:hover": {
        background: "#9D5FCC",
      },
    },
  },
  passReset: {
    textAlign: "right",
    "& span": {
      fontSize: 13,
      color: "#808080",
      cursor: "pointer",
      "&:hover": {
        textDecoration: "underline",
      },
    },
  },
  version: {
    position: "absolute",
    bottom: "0.5em",
    left: "1em",
    lineHeight: 1,
  },
}));

const PageLogin = (props) => {
  const classes = useStyles();
  const [username, setUsername] = React.useState("");
  const [password, setPassword] = React.useState("");
  const [email, setEmail] = React.useState("");
  const [view, setView] = React.useState(LOGIN_VIEW); // login, pass-reset
  const [showPassword, setShowPassword] = React.useState(false);
  const [otp, setOtp] = React.useState("");
  const { appVersion } = props.appInfo;

  React.useEffect(() => {
    props.checkForUpdates();
    // eslint-disable-next-line
  }, []);

  React.useEffect(() => {
    if (props.passwordFieldShouldReset) {
      setPassword("");
    }
    props.setPasswordFieldShouldReset(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.passwordFieldShouldReset]);

  React.useEffect(() => {
    if (!props.otpRequired) {
      setOtp("");
    }
  }, [props.otpRequired]);

  const handleUsernameChange = (event) => {
    setUsername(event.target.value);
  };

  const handleClickShowPassword = () => {
    setShowPassword(!showPassword);
  };

  const handleMouseDownPassword = (event) => {
    event.preventDefault();
  };

  const handlePasswordChange = (event) => {
    setPassword(event.target.value);
  };

  const handleResetEmailChange = (event) => {
    setEmail(event.target.value.toLowerCase());
  };

  const submitForm = (event) => {
    event.preventDefault();
    props.submitLoginForm(username, password, otp);
  };

  const passResetSubmit = async (event) => {
    event.preventDefault();
    try {
      await sendPassResetLink(email);
      setEmail("");
      setView(LOGIN_VIEW);
      props.notify(
        "Reset password link will be sent to the email provided, if it exists in our database."
      );
    } catch (err) {
      console.log(err);
      props.notify("Error sending password reset link", "error");
    }
  };

  const renderForgotPassword = () => {
    const valid = validateEmail(email);
    const error = valid || !email ? "" : "Please enter valid e-mail address";
    return (
      <form onSubmit={passResetSubmit}>
        <div className="mb-3">
          <FormControl size="small" className="w-100">
            <TextField
              autoComplete="hidden"
              size="small"
              required
              variant="outlined"
              label="E-mail address"
              fullWidth
              placeholder="Enter your e-mail address"
              type="email"
              value={email}
              onChange={handleResetEmailChange}
              error={!!error}
              helperText={error}
            />
          </FormControl>
        </div>
        <div className={classes.passReset}>
          <span onClick={() => setView(LOGIN_VIEW)}>Sign in</span>
        </div>

        <div className={classes.actions}>
          <Button
            type="submit"
            variant="contained"
            size="large"
            className="my-2"
          >
            Send reset link
          </Button>
        </div>
      </form>
    );
  };

  return (
    <div className={classes.pageWrap}>
      <Card className={classes.card}>
        <div className={classes.branding}>
          <img src={IconAms} alt="Asset Management Icon" />
        </div>
        <div className={classes.branding2}>
          <img src={ptsLogo} alt="PTS logo" />
        </div>
        <CardContent className={classes.content}>
          {view === "login" && (
            <form onSubmit={submitForm}>
              <div className="mb-3">
                <FormControl size="small" className="w-100">
                  <TextField
                    required
                    variant="outlined"
                    label="Username"
                    fullWidth
                    placeholder="Enter your username"
                    type="text"
                    value={username}
                    onChange={handleUsernameChange}
                    autoComplete="username"
                    size="small"
                    disabled={props.otpRequired}
                    InputProps={{
                      className: classes.input,
                    }}
                  />
                </FormControl>
              </div>
              <div className="mb-3">
                <FormControl size="small" className="w-100">
                  <TextField
                    inputRef={(input) =>
                      props.passwordFieldShouldReset === true && input?.focus()
                    }
                    required
                    variant="outlined"
                    label="Password"
                    fullWidth
                    placeholder="Enter your password"
                    type={showPassword ? "text" : "password"}
                    value={password}
                    onChange={handlePasswordChange}
                    autoComplete="current-password"
                    size="small"
                    disabled={props.otpRequired}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            aria-label="toggle password visibility"
                            onClick={handleClickShowPassword}
                            onMouseDown={handleMouseDownPassword}
                          >
                            {showPassword ? <Visibility /> : <VisibilityOff />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                </FormControl>
              </div>
              {props.otpRequired && (
                <div className="mb-3">
                  <FormControl size="small" className="w-100">
                    <TextField
                      required
                      variant="outlined"
                      label="OTP"
                      fullWidth
                      placeholder="Enter your OTP"
                      type="text"
                      value={otp}
                      onChange={(e) => setOtp(e.target.value)}
                      size="small"
                    />
                  </FormControl>
                </div>
              )}
              <div className={classes.passReset}>
                <span onClick={() => setView(PASSWORD_RESET_VIEW)}>
                  Password reset
                </span>
              </div>
              <div className={classes.actions}>
                <Button
                  type="submit"
                  color="primary"
                  variant="contained"
                  size="large"
                  className={classes.btn}
                >
                  Login
                </Button>
              </div>
            </form>
          )}
          {view === "pass-reset" && renderForgotPassword()}
          <div className={classes.version}>{appVersion}</div>
        </CardContent>
      </Card>
    </div>
  );
};

const mapStateToProps = (state) => ({
  themeMode: state.theme.mode,
  appInfo: state.appInfo,
  passwordFieldShouldReset: state.user.passwordFieldShouldReset,
  otpRequired: state.user.otpRequired,
});

export default connect(mapStateToProps, {
  submitLoginForm,
  checkForUpdates,
  notify,
  setPasswordFieldShouldReset,
})(PageLogin);
